import type { Id, IsoDateTime } from "../primitives";
import type { ShipmentStatus } from "../types/orders";
import { RETURNING_STAGES, type ShipmentStage } from "./tracking";

/**
 * Receiving returned parcels (#529): the vocabulary the server writes and the
 * receiving screens read.
 *
 * A return is **received per shipment**, once. What the team checks is each
 * line of the parcel: good, damaged or missing, with quantities that can be
 * split. Confirming puts the good quantities back into stock through the
 * ordinary stock adjustment write (reason "RTO received", one adjustment per
 * line), tags the order `RTO Received` in Shopify, and records the receipt in
 * the ERP. Nothing else is written to Shopify: not a cancellation, not a
 * refund, not Shopify's "damaged" quantity.
 */

/** The permission that lets someone receive returns. Administrator and Warehouse Manager hold it. */
export const RECEIVE_RETURNS_PERMISSION = "orders.returns.receive";

/**
 * The tag a received order carries in Shopify. #465 will replace it with the
 * stage tag `ERP: RTO received`; until it ships, this is the tag.
 */
export const RTO_RECEIVED_TAG = "RTO Received";

/** The ERP's own word for the stock adjustment's reason (`AdjustmentReason`). */
export const RTO_RECEIVED_ADJUSTMENT_REASON = "rto_received";

/**
 * The stages a parcel can be received from: after a failed delivery attempt,
 * while it is coming back, and once the courier says it is back. Never earlier
 * and never delivered, cancelled or lost.
 */
export const RECEIVABLE_STAGES: readonly ShipmentStage[] = [
  "attempt_failed",
  ...RETURNING_STAGES,
  "rto_delivered",
];

/**
 * A return a person added by hand (#594): a parcel that came back without the
 * courier marking it RTO. Who, when and why, the reason required.
 */
export interface ManualReturn {
  /** Null once that account is deleted. */
  addedByName: string | null;
  addedAt: IsoDateTime;
  reason: string;
}

/** The longest reason an added return takes: one line. */
export const MANUAL_RETURN_REASON_MAX = 200;

/**
 * Whether a shipment belongs in Returns to receive: a failed or returning stage,
 * or — the backlog tracking has no stage for — Shopify's own "not delivered",
 * or one a person added by hand (#594) on an order that is not cancelled.
 * Passing `manual` says the shipment carries the manual-return mark. Whether it
 * was already received is not decided here: a receipt exists once per shipment
 * and the list and the confirm check it themselves.
 * The API builds the same predicate in SQL; a test holds the two to one answer.
 */
export function isReceivableShipment(
  stage: ShipmentStage | null,
  shopifyStatus: ShipmentStatus | null,
  manual: { orderCancelled: boolean } | null = null,
): boolean {
  if (manual !== null && !manual.orderCancelled) return true;
  if (stage !== null) return RECEIVABLE_STAGES.includes(stage);
  return shopifyStatus === "not_delivered";
}

/**
 * Why a shipment cannot be added as a manual return, or null when it can
 * (#594). One rule for the server, the picker and the fixture: not on a
 * cancelled order, not once received, not one already listed (by the courier's
 * stage or an earlier add), and not a shipment that was itself cancelled.
 */
export function manualReturnRefusal(shipment: {
  orderCancelled: boolean;
  stage: ShipmentStage | null;
  shopifyStatus: ShipmentStatus | null;
  received: boolean;
  alreadyAdded: boolean;
}): string | null {
  if (shipment.received) return "This parcel was already received.";
  if (shipment.alreadyAdded) return "This return was already added.";
  if (shipment.orderCancelled) {
    return "This order is cancelled, so a return cannot be added to it.";
  }
  if (shipment.stage === "cancelled") {
    return "This shipment was cancelled, so there is no parcel to receive.";
  }
  if (isReceivableShipment(shipment.stage, shipment.shopifyStatus)) {
    return "This parcel is already under Returns to receive.";
  }
  return null;
}

/** One shipment the Add a return picker offers, and whether it can be added. */
export interface ManualReturnCandidateShipment {
  id: Id;
  name: string | null;
  courier: string | null;
  trackingNumber: string | null;
  stage: ShipmentStage | null;
  shopifyStatus: ShipmentStatus | null;
  /** Null when it can be added, else why not. */
  refusal: string | null;
}

/** An order the Add a return picker found by its number, with its shipments. */
export interface ManualReturnCandidate {
  orderId: Id;
  orderNumber: string;
  cancelled: boolean;
  shipments: ManualReturnCandidateShipment[];
}

/** What a line of a returned parcel can turn out to be. */
export const RETURN_LINE_OUTCOMES = ["good", "damaged", "missing"] as const;
export type ReturnLineOutcome = (typeof RETURN_LINE_OUTCOMES)[number];

export const MAX_RETURN_REASON_LENGTH = 500;
export const MAX_RETURN_NOTE_LENGTH = 1000;
export const MAX_RETURN_PHOTOS_PER_LINE = 5;
/** Per photo. A phone's camera at full size is a few megabytes. */
export const MAX_RETURN_PHOTO_BYTES = 10 * 1024 * 1024;

/**
 * What a photo picker offers. JPEG, PNG, WebP and GIF only: asking for exactly
 * these makes a phone convert its own format to JPEG, where `image/*` can hand
 * over a file the server cannot read.
 */
export const RETURN_PHOTO_ACCEPT = "image/jpeg,image/png,image/webp,image/gif";

/** One tap each, so a reason does not have to be typed on a phone. Free text is still allowed. */
export const DAMAGED_REASON_SUGGESTIONS: readonly string[] = [
  "Box crushed",
  "Item broken",
  "Used or opened",
  "Water damage",
  "Scratched or marked",
];
export const MISSING_REASON_SUGGESTIONS: readonly string[] = [
  "Not in the box",
  "Empty parcel",
  "Wrong item inside",
  "Parcel opened",
];

/** What the screen says on a received order, where Shopify might restock it a second time. */
export const STOCK_ALREADY_RETURNED_NOTICE =
  "Stock already returned. If you cancel this order in Shopify, untick Restock inventory.";

/** What the Cancel order dialog says about a received order. */
export const CANCEL_RECEIVED_ORDER_NOTICE =
  "This return was received and its stock put back, so Restock inventory is off and will be sent off.";

export type ReturnPayment = "cod" | "prepaid";

/** One row of Returns to receive: a shipment that failed or is coming back. */
export interface ReturnToReceive {
  /** The shipment's id: a return is received per shipment. */
  id: Id;
  orderId: Id;
  orderNumber: string;
  /** `#EL1426-F1`. */
  shipmentName: string | null;
  courier: string | null;
  /** Normalised for the courier filter: `delhivery`, `bluedart`. */
  courierKey: string | null;
  trackingNumber: string | null;
  /** Null for the backlog tracking has no stage for; `shopifyStatus` is then "not delivered". */
  stage: ShipmentStage | null;
  shopifyStatus: ShipmentStatus | null;
  /** When the parcel started coming back (or, failing that, first failed). Null with no scans. */
  returningSince: IsoDateTime | null;
  /** Whole days since `returningSince`. Null only when the shipment has no date at all. */
  returningDays: number | null;
  /**
   * True when `returningSince` is an estimate, not a scan: the shipment has a
   * stage but no scan of the return to date it by, so it is the newest scan
   * time it holds, else Shopify's last update of the fulfilment, else the day
   * it shipped. The screen then says "about N days".
   */
  returningApproximate: boolean;
  /** Whether the courier says the parcel is back (stage RTO delivered). */
  rtoDelivered: boolean;
  /** Units in the parcel. */
  itemCount: number;
  warehouseName: string | null;
  payment: ReturnPayment | null;
  /** Set when a person added this return by hand (#594). */
  manual: ManualReturn | null;
}

/** A line of a returned parcel, as the receiving screen lists it. */
export interface ReturnParcelLine {
  orderLineItemId: Id;
  variantId: Id | null;
  sku: string | null;
  /** The product's title. */
  title: string;
  variantTitle: string | null;
  imageUrl: string | null;
  /** Units of this line in this parcel. */
  quantity: number;
  /** Whether good units can be put back: the line needs a mirrored variant. */
  restockable: boolean;
  /** What is available now at each warehouse that can take stock, to help decide. */
  stock: { warehouseId: Id; available: number }[];
}

/** Something the person should know before confirming. The screen still allows it. */
export interface ReturnParcelWarning {
  kind:
    | "not_rto_delivered"
    | "order_cancelled"
    | "earlier_restock"
    | "no_tracking_stage";
  message: string;
}

export interface ReturnWarehouseChoice {
  id: Id;
  name: string;
  code: string | null;
}

/** A parcel opened for receiving. */
export interface ReturnParcel {
  shipmentId: Id;
  shipmentName: string | null;
  orderId: Id;
  orderNumber: string;
  courier: string | null;
  trackingNumber: string | null;
  stage: ShipmentStage | null;
  shopifyStatus: ShipmentStatus | null;
  rtoDelivered: boolean;
  returningDays: number | null;
  /** True when `returningDays` is an estimate: see `ReturnToReceive.returningApproximate`. */
  returningApproximate: boolean;
  payment: ReturnPayment | null;
  /** The order's total, so a prepaid parcel can say what is to be refunded. */
  orderTotal: string;
  currency: string;
  /** Whether this shipment can be received now: failed or returning. */
  receivable: boolean;
  /** Set when a person added this return by hand (#594). */
  manual: ManualReturn | null;
  /** The warehouse the parcel left, where the mirror knows it. */
  shippedFrom: ReturnWarehouseChoice | null;
  /** Where the good units can be put back: warehouses mapped to a Shopify location. */
  warehouses: ReturnWarehouseChoice[];
  defaultWarehouseId: Id | null;
  lines: ReturnParcelLine[];
  warnings: ReturnParcelWarning[];
  /** Set when the parcel has already been received: the screen shows it read-only. */
  receipt: ReturnReceipt | null;
}

/** Where a line's restock stands. */
export type ReturnRestockStatus = "not_needed" | "pending" | "accepted";

/** Where the tag write stands. */
export type ReturnTagStatus = "pending" | "accepted";

/**
 * `complete` once every good line is accepted by Shopify and the tag is on;
 * `restock_pending` while a good line still waits on Shopify; `tag_pending`
 * when the stock is in and only the tag is not.
 */
export type ReturnReceiptStatus =
  "complete" | "restock_pending" | "tag_pending";

export interface ReturnPhoto {
  id: Id;
  filename: string;
}

export interface ReturnReceiptLine {
  id: Id;
  orderLineItemId: Id | null;
  sku: string | null;
  title: string;
  variantTitle: string | null;
  quantity: number;
  good: number;
  damaged: number;
  missing: number;
  damagedReason: string | null;
  missingReason: string | null;
  photos: ReturnPhoto[];
  restockStatus: ReturnRestockStatus;
  /** Shopify's own words for why the last attempt did not go through. */
  restockError: string | null;
  /** The stock adjustment the last attempt produced, shown in Inventory > Adjustments. */
  restockAdjustmentId: Id | null;
  restockAttempts: number;
  /**
   * Another request is putting this line in stock right now: it is pending and
   * either claimed a moment ago or recorded a moment ago and not yet tried. A
   * screen shows "being put back" for it, not "pending", and offers no Retry.
   * False once a try has been refused, or a claim has gone stale (a request that
   * died), which is when Retry is the right thing.
   */
  restockInProgress: boolean;
}

/**
 * Shopify recorded a restock on an order the ERP had already received: the
 * stock may have gone in twice. A row on the order, and a follow-up task.
 */
export interface ReturnRestockFlag {
  id: Id;
  receiptId: Id;
  detectedAt: IsoDateTime;
  /** When Shopify made the refund that restocked. */
  refundedAt: IsoDateTime | null;
  /** Units the refund put back. */
  restockedQuantity: number;
  taskId: Id | null;
}

export interface ReturnReceipt {
  id: Id;
  shipmentId: Id;
  shipmentName: string | null;
  orderId: Id;
  orderNumber: string;
  warehouseId: Id;
  warehouseName: string | null;
  receivedByUserId: Id;
  receivedByName: string | null;
  receivedAt: IsoDateTime;
  note: string | null;
  status: ReturnReceiptStatus;
  tagStatus: ReturnTagStatus;
  tagError: string | null;
  /** The tag is being written right now, by another request. See `ReturnReceiptLine.restockInProgress`. */
  tagInProgress: boolean;
  /** Any line or the tag is in progress: show it, refresh it, and offer no Retry yet. */
  processing: boolean;
  /** Whether the courier had marked the parcel RTO delivered when it was received. */
  courierMarkedDelivered: boolean;
  /** A prepaid order: the refund is done in Shopify and the ERP never moves money. */
  prepaid: boolean;
  /** The "Refund order <number>" task a prepaid receipt creates. */
  refundTaskId: Id | null;
  /** The task made from the damaged and missing lines, when someone asked for one. */
  followUpTaskId: Id | null;
  lines: ReturnReceiptLine[];
  restockFlags: ReturnRestockFlag[];
}

/** A photo as a screen submits it: raw bytes, the one shape a browser and a test share. */
export interface ReturnPhotoInput {
  filename: string;
  contentType: string;
  data: Uint8Array;
}

export interface ReceiveReturnLineInput {
  orderLineItemId: Id;
  good: number;
  damaged: number;
  missing: number;
  damagedReason?: string | null;
  missingReason?: string | null;
  /** Required with damaged units. */
  photos?: ReturnPhotoInput[];
}

export interface ReceiveReturnInput {
  /** Where the good units go back. */
  warehouseId: Id;
  note?: string | null;
  /** Every line of the parcel, each accounted for in full. */
  lines: ReceiveReturnLineInput[];
}

/**
 * Why the input is wrong, per line, or `null` when it can be sent: the checks
 * the screen runs before the button enables, and the server runs again. One
 * function so the two cannot disagree about what is refused.
 *
 * `shipped` is each line's quantity in the parcel, by `orderLineItemId`.
 */
export function validateReceiveInput(
  input: ReceiveReturnInput,
  shipped: ReadonlyMap<Id, number>,
): { message: string; fields: Record<string, string> } | null {
  const fields: Record<string, string> = {};
  const seen = new Set<Id>();

  const note = input.note?.trim() ?? "";
  if (note.length > MAX_RETURN_NOTE_LENGTH) {
    fields.note = `At most ${String(MAX_RETURN_NOTE_LENGTH)} characters.`;
  }

  for (const line of input.lines) {
    const key = `lines.${line.orderLineItemId}`;
    if (!shipped.has(line.orderLineItemId)) {
      fields[key] = "This item is not in the parcel.";
      continue;
    }
    if (seen.has(line.orderLineItemId)) {
      fields[key] = "This item is listed twice.";
      continue;
    }
    seen.add(line.orderLineItemId);

    const quantity = shipped.get(line.orderLineItemId) ?? 0;
    const counts = [line.good, line.damaged, line.missing];
    if (counts.some((count) => !Number.isInteger(count) || count < 0)) {
      fields[key] = "Quantities are whole numbers, zero or more.";
      continue;
    }
    const total = line.good + line.damaged + line.missing;
    if (total > quantity) {
      fields[key] =
        `Only ${String(quantity)} shipped; ${String(total)} counted.`;
      continue;
    }
    if (total < quantity) {
      fields[key] =
        `${String(quantity - total)} of ${String(quantity)} not accounted for: mark each as good, damaged or missing.`;
      continue;
    }
    if (line.damaged > 0) {
      if ((line.damagedReason?.trim() ?? "") === "") {
        fields[`${key}.damagedReason`] = "Say what is wrong with it.";
      } else if (
        (line.damagedReason?.trim().length ?? 0) > MAX_RETURN_REASON_LENGTH
      ) {
        fields[`${key}.damagedReason`] =
          `At most ${String(MAX_RETURN_REASON_LENGTH)} characters.`;
      }
      if ((line.photos?.length ?? 0) === 0) {
        fields[`${key}.photos`] = "A photo of the damaged item is required.";
      } else if ((line.photos?.length ?? 0) > MAX_RETURN_PHOTOS_PER_LINE) {
        fields[`${key}.photos`] =
          `At most ${String(MAX_RETURN_PHOTOS_PER_LINE)} photos.`;
      }
    }
    if (line.missing > 0) {
      if ((line.missingReason?.trim() ?? "") === "") {
        fields[`${key}.missingReason`] = "Say what happened to it.";
      } else if (
        (line.missingReason?.trim().length ?? 0) > MAX_RETURN_REASON_LENGTH
      ) {
        fields[`${key}.missingReason`] =
          `At most ${String(MAX_RETURN_REASON_LENGTH)} characters.`;
      }
    }
  }

  for (const id of shipped.keys()) {
    if (!seen.has(id) && !fields[`lines.${id}`]) {
      fields[`lines.${id}`] = "This item has not been checked.";
    }
  }

  // Nothing checked at all: a receipt of an empty parcel records nothing.
  if (
    Object.keys(fields).length === 0 &&
    input.lines.every((line) => line.good + line.damaged + line.missing === 0)
  ) {
    return {
      message: "Count at least one item as good, damaged or missing.",
      fields: { lines: "Count at least one item." },
    };
  }

  return Object.keys(fields).length === 0
    ? null
    : { message: "Check the highlighted items.", fields };
}

/** One damaged or missing line of a receipt, as the report lists it. */
export interface ReturnLossRow {
  /** `<receiptLineId>:<outcome>`, unique per row. */
  id: string;
  receiptId: Id;
  receivedAt: IsoDateTime;
  orderId: Id;
  orderNumber: string;
  outcome: "damaged" | "missing";
  sku: string | null;
  /** Product and variant, as the line named them. */
  item: string;
  quantity: number;
  reason: string;
  /** The first photo, when there is one. */
  photoId: Id | null;
  photoCount: number;
  receivedByName: string | null;
}

/** What Settings holds for returns: who the refund task goes to, and where tasks are filed. */
export interface ReturnsSettings {
  /** Assignee of "Refund order <number>" on a prepaid receipt. Null leaves it unassigned. */
  refundAssigneeUserId: Id | null;
  /** The project the tasks go in. Null uses (or creates) a project named Returns. */
  taskProjectId: Id | null;
}

export type ReturnsSettingsPatch = Partial<ReturnsSettings>;

/** The title of the task a prepaid receipt creates. */
export function refundTaskTitle(orderNumber: string): string {
  return `Refund order ${orderNumber}`;
}

/** The title of the task made from a receipt's damaged and missing lines. */
export function damagedTaskTitle(orderNumber: string): string {
  return `Damaged or missing items on returned order ${orderNumber}`;
}

/** The title of the task a possible double restock creates. */
export function doubleRestockTaskTitle(orderNumber: string): string {
  return `Possible double restock on order ${orderNumber}`;
}
